import { Grid, Typography, Box, Alert } from "@mui/material";
import { useEffect, useState, useRef, useCallback } from "react";
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
} from "react-hook-form";
import http from "../../../commonFunctions/httpClient";
import WSTextField from "../../../Components/TextBox/WSTextBox";
import config from "../../../Config/config.json";
import { buildImageUrl } from "../../../commonFunctions/CommonFunctions";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";

interface LabCategoryDrawerProps {
  control?: Control<any>;
  errors?: FieldErrors;
  register: UseFormRegister<any>;
  id: any;
  reset: UseFormReset<any>;
  setValue: UseFormSetValue<any>;
  onCancel: () => void;
  onSuccess?: () => void;
}

const LabCategoryDrawer: React.FC<LabCategoryDrawerProps> = ({
  control,
  id,
  register,
  reset,
  setValue,
  onCancel,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);
  const [nameValue, setNameValue] = useState("");

  const imageRef = useRef<HTMLInputElement>(null);

  const loadCategoryData = useCallback(
    async (itemId: string) => {
      try {
        const response: any = await http.get(`lab/LabCategory/${itemId}`);
        const data = response?.data || response;
        if (data) {
          const name = data.name || "";
          setNameValue(name);
          setValue("name", name);

          // Try all common field names the API might return for the image
          const imgPath =
            data.imageUrl ||
            data.imgUrl ||
            data.image ||
            data.imageFile ||
            data.imagePath ||
            data.categoryImage ||
            data.categoryImageUrl ||
            "";

          if (imgPath) {
            setImagePreview(buildImageUrl(imgPath, config.imgUrl));
          } else {
            setImagePreview(null);
          }
        }
      } catch (error: any) {
        console.error("Error loading category:", error);
      }
    },
    [setValue]
  );

  useEffect(() => {
    // Reset state on every id change (covers switching between items or add→edit)
    setImagePreview(null);
    setNameValue("");
    setFileSizeError(null);

    const editMode = id && id.toString().startsWith("labCategoryEdit-");
    setIsEditMode(editMode);

    if (editMode) {
      const itemId = id.toString().replace("labCategoryEdit-", "");
      setCategoryId(itemId);
      loadCategoryData(itemId);
    }
  }, [id, loadCategoryData]);

  const handleImageClick = () => {
    imageRef.current?.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileSizeError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setFileSizeError("Image must be less than 2MB.");
      e.target.value = "";
      return;
    }
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFileSizeError(null);

    const imageFile = imageRef.current?.files?.[0];
    if (imageFile && imageFile.size > 2 * 1024 * 1024) {
      setFileSizeError("Image must be less than 2MB.");
      return;
    }

    const form = e.target as HTMLFormElement;
    const nameInput = form.querySelector<HTMLInputElement>('[name="name"]');
    const nameVal = nameInput?.value || nameValue;

    if (!nameVal?.trim()) {
      setFileSizeError("Category name is required.");
      return;
    }

    const formData = new FormData();
    formData.append("Name", nameVal.trim());
    if (imageFile) {
      formData.append("imageFile", imageFile);
    }

    try {
      setLoading(true);
      let response: any;
      if (isEditMode && categoryId) {
        response = await http.putFormData(`lab/LabCategory/${categoryId}`, formData);
      } else {
        response = await http.postFormData("lab/LabCategory", formData);
      }

      const success =
        response?.success === true ||
        !!response?.data ||
        response?.message?.toLowerCase?.().includes("success") ||
        response?.id ||
        !response?.errors;

      if (success) {
        reset();
        setImagePreview(null);
        setNameValue("");
        onCancel();
        onSuccess?.();
      } else {
        setFileSizeError(response?.message || "Failed to save. Please try again.");
      }
    } catch (error: any) {
      console.error("Submit error:", error);
      setFileSizeError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to save. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form id={id} onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        {fileSizeError && (
          <Grid item xs={12}>
            <Alert severity="error">{fileSizeError}</Alert>
          </Grid>
        )}

        {/* Image Upload */}
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: "#555" }}>
            Category Image
          </Typography>
          <Box
            onClick={handleImageClick}
            sx={{
              width: 160,
              height: 160,
              border: "2px dashed #1976d2",
              borderRadius: "12px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              backgroundColor: "#f8faff",
              overflow: "hidden",
              "&:hover": {
                backgroundColor: "#e3f2fd",
                borderColor: "#1565c0",
              },
            }}
          >
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Category"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <>
                <AddPhotoAlternateIcon sx={{ color: "#1976d2", fontSize: 36, mb: 0.5 }} />
                <Typography variant="caption" sx={{ color: "#1976d2", textAlign: "center" }}>
                  Click to upload
                  <br />
                  (max 2MB)
                </Typography>
              </>
            )}
          </Box>
          <input
            ref={imageRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            style={{ display: "none" }}
          />
        </Grid>

        {/* Name Field */}
        <Grid item xs={12}>
          <WSTextField
            control={control}
            id="name"
            name="name"
            label="Category Name *"
            rules={{ required: true }}
          />
        </Grid>

        {loading && (
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ color: "#666" }}>
              {isEditMode ? "Updating..." : "Saving..."}
            </Typography>
          </Grid>
        )}
      </Grid>
    </form>
  );
};

export default LabCategoryDrawer;
